import './CityAutocomplete.css'

import { autoUpdate, flip, offset, size, useFloating } from '@floating-ui/react-dom'
import { ChangeEvent, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

import { api } from '../lib/api'

import { cx } from '../utilities/cx'
import { Icon } from './Icon'
import { Label } from './Label'
import { defaultOverlayRootName, getOverlayRootElement } from './OverlayRoot'

export type CityResult = {
  osmId: number
  osmType: string
  displayName: string
  latitude: number
  longitude: number
  country: string
}

export type CityAutocompleteProps = {
  className?: string
  label?: string
  placeholder?: string
  disabled?: boolean
  hasError?: boolean
  value?: CityResult | null
  onChange?: (value: CityResult | null) => void
  overlayRootName?: string
}

export function CityAutocomplete(props: CityAutocompleteProps) {
  const {
    className,
    label,
    placeholder = 'Search for a city...',
    disabled,
    hasError,
    value,
    onChange,
    overlayRootName = defaultOverlayRootName,
  } = props

  const [inputValue, setInputValue] = useState(value?.displayName ?? '')
  const [results, setResults] = useState<CityResult[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [isFocused, setIsFocused] = useState(false)
  const [showDropdown, setShowDropdown] = useState(false)
  const [selectedIndex, setSelectedIndex] = useState(-1)

  const inputRef = useRef<HTMLInputElement>(null)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const { refs, floatingStyles } = useFloating({
    placement: 'bottom-start',
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(4),
      flip(),
      size({
        apply({ rects, elements }) {
          Object.assign(elements.floating.style, {
            width: `${rects.reference.width}px`,
          })
        },
      }),
    ],
  })

  useEffect(() => {
    if (value?.displayName && value.displayName !== inputValue) {
      setInputValue(value.displayName)
    }
  }, [value])

  const searchCities = async (query: string) => {
    if (query.length < 2) {
      setResults([])
      return
    }

    setIsLoading(true)

    try {
      const response = await api.fetch(`/regions/search?q=${encodeURIComponent(query)}`)

      if (!response.ok) {
        setResults([])
        return
      }

      const data = (await response.json()) as CityResult[]
      setResults(data)
      setSelectedIndex(-1)
    } catch (_err) {
      setResults([])
    } finally {
      setIsLoading(false)
    }
  }

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value
    setInputValue(newValue)
    setShowDropdown(true)

    if (debounceRef.current) {
      clearTimeout(debounceRef.current)
    }

    debounceRef.current = setTimeout(() => {
      searchCities(newValue)
    }, 300)

    if (value) {
      onChange?.(null)
    }
  }

  const handleSelect = (result: CityResult) => {
    setInputValue(result.displayName)
    setResults([])
    setShowDropdown(false)
    onChange?.(result)
    inputRef.current?.blur()
  }

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (!showDropdown || results.length === 0) return

    if (event.key === 'ArrowDown') {
      event.preventDefault()
      setSelectedIndex((prev) => (prev < results.length - 1 ? prev + 1 : prev))
    } else if (event.key === 'ArrowUp') {
      event.preventDefault()
      setSelectedIndex((prev) => (prev > 0 ? prev - 1 : prev))
    } else if (event.key === 'Enter' && selectedIndex >= 0 && results[selectedIndex]) {
      event.preventDefault()
      handleSelect(results[selectedIndex])
    } else if (event.key === 'Escape') {
      setShowDropdown(false)
    }
  }

  const handleFocus = () => {
    setIsFocused(true)
    if (results.length > 0) {
      setShowDropdown(true)
    }
  }

  const handleBlur = () => {
    setIsFocused(false)
    setTimeout(() => setShowDropdown(false), 150)
  }

  const frameClasses = cx('city-autocomplete__frame', {
    'city-autocomplete__frame--focused': !disabled && isFocused,
    'city-autocomplete__frame--disabled': disabled,
    'city-autocomplete__frame--error': hasError,
  })

  return (
    <div className={cx('city-autocomplete', className)}>
      {label && <Label>{label}</Label>}
      <div ref={refs.setReference} className={frameClasses}>
        <div className="city-autocomplete__icon city-autocomplete__icon--left">
          <Icon name="search" />
        </div>
        <input
          ref={inputRef}
          className="city-autocomplete__input"
          type="text"
          placeholder={placeholder}
          value={inputValue}
          disabled={disabled}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={handleFocus}
          onBlur={handleBlur}
        />
        {isLoading && (
          <div className="city-autocomplete__icon city-autocomplete__icon--right">
            <div className="city-autocomplete__spinner" />
          </div>
        )}
        {!isLoading && value && (
          <div className="city-autocomplete__icon city-autocomplete__icon--right city-autocomplete__check">
            <Icon name="check" />
          </div>
        )}
      </div>

      {showDropdown &&
        results.length > 0 &&
        createPortal(
          <div
            ref={refs.setFloating}
            className="city-autocomplete__dropdown"
            style={floatingStyles}
          >
            {results.map((result, index) => (
              <div
                key={`${result.osmType}-${result.osmId}`}
                className={cx('city-autocomplete__option', {
                  'city-autocomplete__option--selected': index === selectedIndex,
                })}
                onMouseDown={() => handleSelect(result)}
                onMouseEnter={() => setSelectedIndex(index)}
              >
                <Icon name="map-pin" />
                <span>{result.displayName}</span>
              </div>
            ))}
          </div>,
          getOverlayRootElement(overlayRootName)!,
        )}
    </div>
  )
}
